import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Skull, FlaskConical, Diamond, Zap, TrendingUp, TrendingDown, ChevronDown, ChevronUp, Timer, Heart, Swords, Shield, Send } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Legend } from "recharts";
import { getRaids, getRaidComparison, sendRaidComparisonDiscord } from "@/lib/api";
import { CLASS_COLORS } from "@/lib/constants";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const RAID_COLORS = { A: "#3b82f6", B: "#ef4444" };

function GradeRing({ grade, label, sublabel, color }) {
  const radius = 44;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (grade / 100) * circumference;
  const gradeColor = grade >= 80 ? "#1eff00" : grade >= 60 ? "#0070dd" : grade >= 40 ? "#ff8800" : "#ff2222";

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative w-28 h-28">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r={radius} fill="none" stroke="#1e293b" strokeWidth="6" />
          <circle cx="50" cy="50" r={radius} fill="none" stroke={gradeColor} strokeWidth="6"
            strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset}
            style={{ transition: "stroke-dashoffset 1s ease-out" }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold font-mono" style={{ color: gradeColor }}>{grade}</span>
          <span className="text-[10px] text-muted-foreground uppercase">Grade</span>
        </div>
      </div>
      <span className="text-sm font-bold" style={{ color }}>{label}</span>
      {sublabel && <span className="text-[10px] text-muted-foreground -mt-1">{sublabel}</span>}
    </div>
  );
}

function StatCompare({ label, valueA, valueB, icon: Icon, suffix = "", higherIsBetter = true, expanded, onClick, children }) {
  const a = typeof valueA === "number" ? valueA : parseFloat(valueA) || 0;
  const b = typeof valueB === "number" ? valueB : parseFloat(valueB) || 0;
  const winnerA = higherIsBetter ? a > b : a < b;
  const winnerB = higherIsBetter ? b > a : b < a;
  const ArrowIcon = higherIsBetter ? ChevronUp : ChevronDown;

  return (
    <>
      <div
        className={`grid grid-cols-[1fr_auto_1fr] gap-4 items-center py-3 border-b border-border/20 cursor-pointer hover:bg-card/30 transition-colors rounded-sm px-2 ${expanded ? "bg-card/30" : ""}`}
        onClick={onClick}
        data-testid={`stat-compare-${label.toLowerCase().replace(/\s/g, "-")}`}
      >
        <div className={`text-right ${winnerA ? "text-green-400 font-bold" : ""}`}>
          <span className="text-lg font-mono">
            {typeof valueA === "number" ? valueA.toLocaleString() : valueA}{suffix}
          </span>
          {winnerA && <ArrowIcon className="h-4 w-4 text-green-500 inline ml-1" />}
        </div>
        <div className="flex flex-col items-center gap-0.5 min-w-[120px]">
          <div className="flex items-center gap-1.5">
            {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
            {expanded ? <ChevronDown className="h-3 w-3 text-muted-foreground" /> : <ChevronUp className="h-3 w-3 text-muted-foreground rotate-90" />}
          </div>
          <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{label}</span>
        </div>
        <div className={`text-left ${winnerB ? "text-green-400 font-bold" : ""}`}>
          <span className="text-lg font-mono">
            {typeof valueB === "number" ? valueB.toLocaleString() : valueB}{suffix}
          </span>
          {winnerB && <ArrowIcon className="h-4 w-4 text-green-500 inline ml-1" />}
        </div>
      </div>
      {expanded && (
        <div className="py-3 px-4 border-b border-border/20 bg-background/40 animate-fade-in">
          {children}
        </div>
      )}
    </>
  );
}

function BossTimesChart({ bossTimesA, bossTimesB, labelA, labelB }) {
  const allBosses = new Set([...Object.keys(bossTimesA || {}), ...Object.keys(bossTimesB || {})]);
  const chartData = [...allBosses].map((boss) => ({
    boss: boss.length > 15 ? boss.substring(0, 15) + "..." : boss,
    [labelA]: bossTimesA?.[boss] || 0,
    [labelB]: bossTimesB?.[boss] || 0,
  }));

  if (chartData.length === 0) return null;

  return (
    <div className="stat-card p-4 mt-4" data-testid="boss-times-chart">
      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
          <XAxis dataKey="boss" tick={{ fontSize: 10, fill: "#64748b" }} />
          <YAxis tick={{ fontSize: 10, fill: "#64748b" }} />
          <RTooltip contentStyle={{ background: "#0f1623", border: "1px solid #1e293b", borderRadius: 4, fontSize: 12 }} />
          <Legend />
          <Bar dataKey={labelA} fill={RAID_COLORS.A} radius={[3, 3, 0, 0]} maxBarSize={30} />
          <Bar dataKey={labelB} fill={RAID_COLORS.B} radius={[3, 3, 0, 0]} maxBarSize={30} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function GradeBar({ label, value, color }) {
  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <span className="text-xs font-mono uppercase" style={{ color }}>{label}</span>
        <span className="text-sm font-bold font-mono">{Math.round(value)}%</span>
      </div>
      <div className="h-3 bg-secondary/50 rounded-sm overflow-hidden">
        <div className="h-full rounded-sm transition-all duration-1000" style={{ width: `${value}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

export default function RaidComparison() {
  const [raids, setRaids] = useState([]);
  const [raidA, setRaidA] = useState("");
  const [raidB, setRaidB] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedMetric, setExpandedMetric] = useState(null);
  const [webhookUrl, setWebhookUrl] = useState(() => localStorage.getItem("discord_webhook_url") || "");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    getRaids().then(setRaids).catch(() => {});
  }, []);

  useEffect(() => {
    if (!raidA || !raidB || raidA === raidB) { setData(null); return; }
    setLoading(true);
    setExpandedMetric(null);
    getRaidComparison(raidA, raidB)
      .then(setData)
      .catch(() => toast.error("Failed to load comparison"))
      .finally(() => setLoading(false));
  }, [raidA, raidB]);

  if (loading && !data) {
    return (
      <div className="p-6 md:p-8 lg:p-12 animate-fade-in flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const a = data?.raid_a;
  const b = data?.raid_b;
  const labelA = a ? (a.date || "Raid A") : "Raid A";
  const labelB = b ? (b.date || "Raid B") : "Raid B";

  function toggle(key) {
    setExpandedMetric(expandedMetric === key ? null : key);
  }

  return (
    <div className="p-6 md:p-8 lg:p-12 animate-fade-in" data-testid="raid-comparison-page">
      <h1 className="heading text-4xl md:text-5xl font-bold tracking-tight uppercase mb-2">
        Raid vs Raid
      </h1>
      <p className="text-muted-foreground mb-6">Compare two raids — track progress week over week</p>

      {/* Raid Selectors */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div>
          <label className="text-[10px] font-mono uppercase tracking-wider mb-1 block" style={{ color: RAID_COLORS.A }}>
            Raid A (Older)
          </label>
          <Select value={raidA} onValueChange={setRaidA}>
            <SelectTrigger data-testid="raid-a-selector" className="bg-card/50 text-sm h-8">
              <SelectValue placeholder="Select raid..." />
            </SelectTrigger>
            <SelectContent>
              {raids.map((r) => (
                <SelectItem key={r.id} value={r.id}>{r.raid_name} — {r.date}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-wider mb-1 block" style={{ color: RAID_COLORS.B }}>
            Raid B (Newer)
          </label>
          <Select value={raidB} onValueChange={setRaidB}>
            <SelectTrigger data-testid="raid-b-selector" className="bg-card/50 text-sm h-8">
              <SelectValue placeholder="Select raid..." />
            </SelectTrigger>
            <SelectContent>
              {raids.map((r) => (
                <SelectItem key={r.id} value={r.id}>{r.raid_name} — {r.date}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {a && b && (
        <>
          {/* Grade Circles */}
          <div className="flex justify-center items-end gap-12 mb-8">
            <GradeRing grade={Math.round(a.grade)} label={labelA} sublabel={a.raid_name} color={RAID_COLORS.A} />
            <div className="text-3xl font-bold text-muted-foreground mb-8">VS</div>
            <GradeRing grade={Math.round(b.grade)} label={labelB} sublabel={b.raid_name} color={RAID_COLORS.B} />
          </div>

          {/* Head to Head Stats — clickable */}
          <Card className="bg-card/50 border-border mb-6">
            <CardContent className="p-6">
              <div className="grid grid-cols-[1fr_auto_1fr] gap-4 items-center mb-4">
                <h3 className="heading text-base font-semibold uppercase tracking-tight text-right" style={{ color: RAID_COLORS.A }}>{labelA}</h3>
                <span className="text-xs font-mono uppercase text-muted-foreground">Metric</span>
                <h3 className="heading text-base font-semibold uppercase tracking-tight" style={{ color: RAID_COLORS.B }}>{labelB}</h3>
              </div>

              <StatCompare label="Boss Kill Times" icon={Timer}
                valueA={Object.keys(a.boss_times).length} valueB={Object.keys(b.boss_times).length} suffix=" kills"
                expanded={expandedMetric === "boss"} onClick={() => toggle("boss")}>
                <BossTimesDetail a={a} b={b} labelA={labelA} labelB={labelB} />
              </StatCompare>

              <StatCompare label="Avg DPS" icon={Swords} valueA={a.avg_dps} valueB={b.avg_dps}
                expanded={expandedMetric === "dps"} onClick={() => toggle("dps")}>
                <TwoColumnPlayers playersA={a.details.dps_players} playersB={b.details.dps_players} labelA={labelA} labelB={labelB} metric="value" suffix=" DPS" showRole />
              </StatCompare>

              <StatCompare label="Avg HPS" icon={Heart} valueA={a.avg_hps} valueB={b.avg_hps}
                expanded={expandedMetric === "hps"} onClick={() => toggle("hps")}>
                <TwoColumnPlayers playersA={a.details.hps_players} playersB={b.details.hps_players} labelA={labelA} labelB={labelB} metric="value" suffix=" HPS" />
              </StatCompare>

              <StatCompare label="Wipes" icon={Skull} valueA={a.wipes} valueB={b.wipes} higherIsBetter={false}
                expanded={expandedMetric === "wipes"} onClick={() => toggle("wipes")}>
                <TwoColumnList itemsA={a.details.wipe_bosses.map(b => ({ text: b }))} itemsB={b.details.wipe_bosses.map(b => ({ text: b }))} labelA={labelA} labelB={labelB} emptyText="No wipes" emptyColor="text-green-400" itemColor="text-red-400" />
              </StatCompare>

              <StatCompare label="Deaths" icon={Skull} valueA={a.total_deaths} valueB={b.total_deaths} higherIsBetter={false}
                expanded={expandedMetric === "deaths"} onClick={() => toggle("deaths")}>
                <TwoColumnPlayers playersA={a.details.death_players} playersB={b.details.death_players} labelA={labelA} labelB={labelB} metric="deaths" suffix=" deaths" />
              </StatCompare>

              <StatCompare label="Consumables" icon={FlaskConical} valueA={a.consumables_used} valueB={b.consumables_used}
                expanded={expandedMetric === "consumes"} onClick={() => toggle("consumes")}>
                <TwoColumnPlayers playersA={a.details.consume_players} playersB={b.details.consume_players} labelA={labelA} labelB={labelB} metric="count" suffix=" used" />
              </StatCompare>

              <StatCompare label="Enchant %" icon={Zap} valueA={a.enchant_pct} valueB={b.enchant_pct} suffix="%"
                expanded={expandedMetric === "enchants"} onClick={() => toggle("enchants")}>
                <TwoColumnIssues issuesA={a.details.enchant_issues} issuesB={b.details.enchant_issues} labelA={labelA} labelB={labelB} />
              </StatCompare>

              <StatCompare label="Gem %" icon={Diamond} valueA={a.gem_pct} valueB={b.gem_pct} suffix="%"
                expanded={expandedMetric === "gems"} onClick={() => toggle("gems")}>
                <TwoColumnIssues issuesA={a.details.gem_issues} issuesB={b.details.gem_issues} labelA={labelA} labelB={labelB} />
              </StatCompare>
            </CardContent>
          </Card>

          {/* Grade Breakdown */}
          <div className="stat-card p-4">
            <h3 className="heading text-base font-semibold uppercase tracking-tight mb-2">Grade Breakdown</h3>
            <p className="text-xs text-muted-foreground mb-3">Internal grading: Kill Rate (30%) + Enchants (20%) + Speed (25%) + Consumables (15%) + Gems (10%)</p>
            <div className="grid grid-cols-2 gap-3">
              <GradeBar label={labelA} value={a.grade} color={RAID_COLORS.A} />
              <GradeBar label={labelB} value={b.grade} color={RAID_COLORS.B} />
            </div>
          </div>
          {/* Discord Send */}
          <div className="mt-6 stat-card p-4" data-testid="discord-raid-comparison">
            <h3 className="heading text-base font-semibold uppercase tracking-tight mb-3">Share to Discord</h3>
            <div className="flex gap-2">
              <Input data-testid="raid-comparison-webhook-input"
                placeholder="Discord Webhook URL"
                value={webhookUrl}
                onChange={(e) => { setWebhookUrl(e.target.value); localStorage.setItem("discord_webhook_url", e.target.value); }}
                className="bg-background/50 text-sm h-8 flex-1" />
              <Button data-testid="raid-comparison-discord-send" size="sm" disabled={!webhookUrl || sending}
                onClick={async () => {
                  setSending(true);
                  try {
                    await sendRaidComparisonDiscord(webhookUrl, raidA, raidB);
                    toast.success("Raid comparison sent to Discord!");
                  } catch (err) {
                    const msg = err?.response?.data?.detail || "Failed to send";
                    toast.error(msg);
                  }
                  setSending(false);
                }}
                className="gap-1.5 h-8">
                {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                Send
              </Button>
            </div>
          </div>
        </>
      )}

      {!a && !loading && (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <TrendingUp className="h-10 w-10 mb-3 opacity-50" />
          <p className="text-lg">Select two raids to compare progress</p>
        </div>
      )}
    </div>
  );
}

/* ─── Detail Components ─── */

function BossTimesDetail({ a, b, labelA, labelB }) {
  const allBosses = [...new Set([...Object.keys(a.boss_times), ...Object.keys(b.boss_times)])];
  if (allBosses.length === 0) return <p className="text-sm text-muted-foreground text-center">No boss kill data available</p>;

  return (
    <>
      <div className="grid grid-cols-[1fr_auto_1fr] gap-4 items-center mb-2">
        <span className="text-[10px] font-mono uppercase tracking-wider text-right" style={{ color: RAID_COLORS.A }}>{labelA}</span>
        <span className="text-[10px] font-mono uppercase tracking-wider text-center text-muted-foreground">Boss</span>
        <span className="text-[10px] font-mono uppercase tracking-wider" style={{ color: RAID_COLORS.B }}>{labelB}</span>
      </div>
      {allBosses.map((boss) => {
        const tA = a.boss_times[boss];
        const tB = b.boss_times[boss];
        const faster = tA && tB && tB < tA;
        const slower = tA && tB && tB > tA;
        return (
          <div key={boss} className="grid grid-cols-[1fr_auto_1fr] gap-4 items-center py-1.5">
            <span className="text-sm font-mono text-right">{tA ? `${tA}s` : "—"}</span>
            <span className="text-xs text-center text-muted-foreground min-w-[140px] truncate">{boss}</span>
            <span className={`text-sm font-mono ${faster ? "text-green-400 font-bold" : slower ? "text-red-400" : ""}`}>
              {tB ? `${tB}s` : "—"}
              {faster && <TrendingDown className="h-3 w-3 inline ml-1 text-green-400" />}
              {slower && <TrendingUp className="h-3 w-3 inline ml-1 text-red-400" />}
            </span>
          </div>
        );
      })}
      <BossTimesChart bossTimesA={a.boss_times} bossTimesB={b.boss_times} labelA={labelA} labelB={labelB} />
    </>
  );
}

function TwoColumnPlayers({ playersA, playersB, labelA, labelB, metric, suffix, showRole }) {
  return (
    <div className="grid grid-cols-2 gap-6">
      <div>
        <span className="text-[10px] font-mono uppercase tracking-wider mb-2 block" style={{ color: RAID_COLORS.A }}>{labelA} ({playersA.length})</span>
        {playersA.length === 0 ? <p className="text-xs text-muted-foreground">No data</p> :
          playersA.map((p, i) => (
            <div key={`${p.name}-${i}`} className="flex items-center justify-between py-1 px-1 hover:bg-card/30 rounded-sm">
              <span className="text-sm truncate" style={{ color: CLASS_COLORS[p.class] || "#94a3b8" }}>
                {p.name}
                {showRole && p.role === "Tank" && <Shield className="h-3 w-3 inline ml-1 text-muted-foreground" />}
              </span>
              <span className="text-xs font-mono text-muted-foreground ml-2 shrink-0">{p[metric]?.toLocaleString()}{suffix}</span>
            </div>
          ))
        }
      </div>
      <div>
        <span className="text-[10px] font-mono uppercase tracking-wider mb-2 block" style={{ color: RAID_COLORS.B }}>{labelB} ({playersB.length})</span>
        {playersB.length === 0 ? <p className="text-xs text-muted-foreground">No data</p> :
          playersB.map((p, i) => (
            <div key={`${p.name}-${i}`} className="flex items-center justify-between py-1 px-1 hover:bg-card/30 rounded-sm">
              <span className="text-sm truncate" style={{ color: CLASS_COLORS[p.class] || "#94a3b8" }}>
                {p.name}
                {showRole && p.role === "Tank" && <Shield className="h-3 w-3 inline ml-1 text-muted-foreground" />}
              </span>
              <span className="text-xs font-mono text-muted-foreground ml-2 shrink-0">{p[metric]?.toLocaleString()}{suffix}</span>
            </div>
          ))
        }
      </div>
    </div>
  );
}

function TwoColumnList({ itemsA, itemsB, labelA, labelB, emptyText, emptyColor, itemColor }) {
  return (
    <div className="grid grid-cols-2 gap-6">
      <div>
        <span className="text-[10px] font-mono uppercase tracking-wider mb-2 block" style={{ color: RAID_COLORS.A }}>{labelA}</span>
        {itemsA.length === 0 ? <p className={`text-xs ${emptyColor}`}>{emptyText}</p> :
          itemsA.map((item, i) => <p key={i} className={`text-sm py-0.5 ${itemColor}`}>{item.text}</p>)}
      </div>
      <div>
        <span className="text-[10px] font-mono uppercase tracking-wider mb-2 block" style={{ color: RAID_COLORS.B }}>{labelB}</span>
        {itemsB.length === 0 ? <p className={`text-xs ${emptyColor}`}>{emptyText}</p> :
          itemsB.map((item, i) => <p key={i} className={`text-sm py-0.5 ${itemColor}`}>{item.text}</p>)}
      </div>
    </div>
  );
}

function TwoColumnIssues({ issuesA, issuesB, labelA, labelB }) {
  const improved = issuesA.length > issuesB.length;
  const regressed = issuesB.length > issuesA.length;

  return (
    <div className="grid grid-cols-2 gap-6">
      <div>
        <span className="text-[10px] font-mono uppercase tracking-wider mb-2 block" style={{ color: RAID_COLORS.A }}>{labelA} ({issuesA.length} issues)</span>
        {issuesA.length === 0 ? <p className="text-xs text-green-400">All good</p> :
          issuesA.map((issue, i) => (
            <div key={i} className="flex items-center gap-2 py-0.5">
              <span className="text-sm truncate" style={{ color: CLASS_COLORS[issue.class] || "#94a3b8" }}>{issue.name}</span>
              <span className="text-xs text-muted-foreground truncate">{issue.item || issue.slot}</span>
            </div>
          ))
        }
      </div>
      <div>
        <span className="text-[10px] font-mono uppercase tracking-wider mb-2 block" style={{ color: RAID_COLORS.B }}>{labelB} ({issuesB.length} issues)</span>
        {issuesB.length === 0 ? <p className="text-xs text-green-400">All good</p> :
          issuesB.map((issue, i) => (
            <div key={i} className="flex items-center gap-2 py-0.5">
              <span className="text-sm truncate" style={{ color: CLASS_COLORS[issue.class] || "#94a3b8" }}>{issue.name}</span>
              <span className="text-xs text-muted-foreground truncate">{issue.item || issue.slot}</span>
            </div>
          ))
        }
        {improved && <p className="text-xs text-green-400 mt-2 font-mono"><TrendingDown className="h-3 w-3 inline mr-1" />{issuesA.length - issuesB.length} fewer issues</p>}
        {regressed && <p className="text-xs text-red-400 mt-2 font-mono"><TrendingUp className="h-3 w-3 inline mr-1" />{issuesB.length - issuesA.length} more issues</p>}
      </div>
    </div>
  );
}
